import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Col,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";

import { WebhookLog } from "../../../../services/superAdmin/adminWebhookLogService";

interface WebhookLogDetailModalProps {
  isOpen: boolean;
  log: WebhookLog | null;
  loading: boolean;
  onClose: () => void;
  formatDateTime: (value: string) => string;
}

const formatJsonBlock = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "object" && Object.keys(value as object).length === 0) {
    return "";
  }

  return JSON.stringify(value, null, 2);
};

const WebhookLogDetailModal = ({
  isOpen,
  log,
  loading,
  onClose,
  formatDateTime,
}: WebhookLogDetailModalProps) => {
  const { t } = useTranslation();

  const payloadJson = useMemo(
    () => (log ? formatJsonBlock(log.payload) : ""),
    [log]
  );

  const notAvailable = t("admin.webhookLogs.common.notAvailable");

  const formatTenantLabel = (entry: WebhookLog | null): string => {
    if (!entry?.tenant) {
      return entry?.tenant_id
        ? `#${entry.tenant_id}`
        : notAvailable;
    }

    return `#${entry.tenant.id} — ${entry.tenant.name}`;
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>
        {t("admin.webhookLogs.details.title")}
      </ModalHeader>
      <ModalBody>
        {loading ? (
          <div className="d-flex justify-content-center py-4">
            <Spinner color="primary" />
          </div>
        ) : !log ? (
          <p className="text-muted mb-0">{notAvailable}</p>
        ) : (
          <>
            <Row className="g-3 mb-4">
              <Col md={6}>
                <p className="text-muted mb-1 fs-12">
                  {t("admin.webhookLogs.details.date")}
                </p>
                <p className="fw-medium mb-0">{formatDateTime(log.created_at)}</p>
              </Col>
              <Col md={6}>
                <p className="text-muted mb-1 fs-12">
                  {t("admin.webhookLogs.details.tenant")}
                </p>
                <p className="mb-0">{formatTenantLabel(log)}</p>
              </Col>
              <Col md={6}>
                <p className="text-muted mb-1 fs-12">
                  {t("admin.webhookLogs.details.provider")}
                </p>
                <p className="mb-0 font-monospace">{log.provider_slug}</p>
              </Col>
              <Col md={6}>
                <p className="text-muted mb-1 fs-12">
                  {t("admin.webhookLogs.details.direction")}
                </p>
                <Badge
                  color={log.direction === "inbound" ? "soft-info" : "soft-warning"}
                  className="fs-12"
                >
                  {t(`admin.webhookLogs.direction.${log.direction}`, {
                    defaultValue: log.direction,
                  })}
                </Badge>
              </Col>
              <Col md={6}>
                <p className="text-muted mb-1 fs-12">
                  {t("admin.webhookLogs.details.event")}
                </p>
                <p className="mb-0 font-monospace">{log.event}</p>
              </Col>
              <Col md={6}>
                <p className="text-muted mb-1 fs-12">
                  {t("admin.webhookLogs.details.httpStatus")}
                </p>
                <p className="mb-0 font-monospace">
                  {log.http_status ?? notAvailable}
                </p>
              </Col>
              {log.external_event_id ? (
                <Col md={12}>
                  <p className="text-muted mb-1 fs-12">
                    {t("admin.webhookLogs.details.externalEventId")}
                  </p>
                  <p className="mb-0 font-monospace">{log.external_event_id}</p>
                </Col>
              ) : null}
            </Row>

            <div className="mb-3">
              <h6 className="mb-2">{t("admin.webhookLogs.details.payload")}</h6>
              {payloadJson ? (
                <pre className="bg-light border rounded p-3 mb-0 font-monospace fs-12 webhook-log-json">
                  {payloadJson}
                </pre>
              ) : (
                <p className="text-muted mb-0">
                  {t("admin.webhookLogs.details.noPayload")}
                </p>
              )}
            </div>

            <div>
              <h6 className="mb-2">{t("admin.webhookLogs.details.errorMessage")}</h6>
              {log.error_message ? (
                <pre className="bg-light border rounded p-3 mb-0 font-monospace fs-12 webhook-log-json">
                  {log.error_message}
                </pre>
              ) : (
                <p className="text-muted mb-0">
                  {t("admin.webhookLogs.details.noError")}
                </p>
              )}
            </div>
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default WebhookLogDetailModal;
